import clsx from 'clsx'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { IconName, IconNameAndCategoryString } from '../Icon'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import { Chip } from './fields/Chip'
import { Select } from './fields/Select'
import { TextField } from './fields/TextField'
import { SelectOption } from '../settings/Options'

type ActionButtonProps = {
    icon?: IconNameAndCategoryString
    intent: 'primary' | 'secondary'
    disabled?: boolean
    isLoading?: boolean
} & (
    | { label: string; href?: string; onClick?: () => void }
    | {
          buttonLabel: string
          actions: {
              id: string
              label: string
              href?: string
              icon?: IconName
              onClick?: () => void
          }[]
      }
)

type Props = {
    title?: React.ReactNode
    backButton?: {
        label: string
        href?: string
        onClick?: () => void
    }
    dateFilter?: {
        selectedDateFilter?: string
        onDateFilterChange: (dateFilter: string) => void
        dateTypeOptions: SelectOption[]
    }
    viewFilter?: {
        selectedViewFilter?: number
        onViewFilterChange: (viewFilter: string) => void
        viewOptions: SelectOption[]
    }
    searchText?: string
    onSearchTextChange?: (searchText: string) => void
    actionButton?: ActionButtonProps
    secondaryActionButton?: ActionButtonProps
}

export function ActionButton({ actionButton }: { actionButton: Props['actionButton'] }) {
    const navigate = useNavigate()
    const [isOpen, setIsOpen] = useState(false)

    if (!actionButton) {
        return null
    }

    if ('label' in actionButton) {
        if ('href' in actionButton) {
            return (
                <Button
                    leftIcon={actionButton?.icon}
                    size="small"
                    intent={actionButton?.intent}
                    href={actionButton?.href}
                    isLoading={actionButton?.isLoading}
                >
                    {actionButton?.label}
                </Button>
            )
        }
        return (
            <Button
                leftIcon={actionButton?.icon}
                size="small"
                intent={actionButton?.intent}
                onClick={actionButton?.onClick}
                disabled={actionButton?.disabled}
                isLoading={actionButton?.isLoading}
            >
                {actionButton?.label}
            </Button>
        )
    }

    return (
        <Select
            align="end"
            trigger={
                <Button
                    size="small"
                    disabled={actionButton.disabled}
                    isLoading={actionButton.isLoading}
                    intent={actionButton.intent}
                    leftIcon={actionButton.icon}
                    rightIcon={
                        <Icon
                            icon="chevronDown.medium"
                            className={`transition-transform duration-200 ${isOpen ? '-rotate-180' : 'rotate-0'}`}
                            shouldInheritColor
                        />
                    }
                >
                    {actionButton.buttonLabel}
                </Button>
            }
            onOpenChange={setIsOpen}
            options={actionButton.actions.map((action) => ({
                id: action.id,
                title: action.label,
                action: action,
                icon: action.icon,
            }))}
            onSelectOption={(option) => {
                const action = option.action
                if (action.href) {
                    navigate(action.href)
                }
                else if (action.onClick) {
                    action.onClick()
                }
            }}
        />
    )
}

export function ListHeader({
    title,
    searchText,
    onSearchTextChange,
    dateFilter,
    viewFilter,
    actionButton,
    secondaryActionButton,
    backButton,
}: Props) {
    const [isDateFilterOpen, setIsDateFilterOpen] = useState(false)

    return (
        <div className="mr-8 ml-4">
            {Boolean(backButton) && Boolean(backButton?.href || backButton?.onClick) && (
                <Button
                    leftIcon="chevronLeft.medium"
                    intent="tertiaryLink"
                    href={backButton?.href}
                    onClick={backButton?.onClick}
                    isDense
                >
                    {backButton?.label}
                </Button>
            )}

            <div className="mt-3 flex items-center justify-between">
                <Heading size="md" className="ml-4">
                    {title}
                </Heading>

                {/* filtering options */}
                <div className="flex items-center gap-2">
                    {onSearchTextChange && (
                        <TextField
                            size="small"
                            placeholder="Search"
                            value={searchText}
                            onChange={(e) => onSearchTextChange(e.target.value)}
                            onClear={() => onSearchTextChange('')}
                            icon="magnifier.small"
                            className="w-[160px] cursor-text"
                        />
                    )}
                    {dateFilter && (
                        <Select
                            size="small"
                            options={dateFilter.dateTypeOptions.map((option) => ({
                                id: option.value,
                                title: option.label,
                            }))}
                            selectedOptionID={dateFilter.selectedDateFilter}
                            onSelectOption={(option) => {
                                if (option) {
                                    dateFilter.onDateFilterChange(option.id)
                                }
                            }}
                            align="end"
                            trigger={
                                <Button
                                    size="small"
                                    className={clsx(
                                        'border-gray-500 font-normal text-gray-700',
                                        'outline-primary-700 outline-0',
                                        'focus:border-primary-700 focus:outline-2',
                                        'hover:border-primary-700 hover:bg-white hover:from-white hover:to-white hover:text-gray-700 hover:outline-1',
                                        isDateFilterOpen && 'border-primary-700 outline-2'
                                    )}
                                    rightIcon={
                                        <Icon
                                            icon="chevronDown.medium"
                                            className={`transition-transform duration-200 ${isDateFilterOpen ? '-rotate-180' : 'rotate-0'}`}
                                            shouldInheritColor
                                        />
                                    }
                                >
                                    {
                                        dateFilter.dateTypeOptions.find(
                                            (option) =>
                                                option.value === dateFilter.selectedDateFilter
                                        )?.label
                                    }
                                </Button>
                            }
                            onOpenChange={setIsDateFilterOpen}
                            className="max-h-[500px] overflow-y-auto"
                        />
                    )}
                    <div className="flex items-center gap-2">
                        <ActionButton actionButton={secondaryActionButton} />
                        <ActionButton actionButton={actionButton} />
                    </div>
                </div>
            </div>

            {viewFilter && (
                <div className="m-4 mb-6 flex flex-wrap items-center gap-2">
                    {viewFilter.viewOptions?.map((option: SelectOption) => (
                        <Chip
                            size="small"
                            key={option.value}
                            className="cursor-pointer bg-white"
                            title={option.label}
                            isSelected={option.value === viewFilter.selectedViewFilter?.toString()}
                            onClick={() => {
                                viewFilter.onViewFilterChange(option.value)
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}
